import { ReportExportButtons } from '../components/ReportExportButtons'
import { useCallback, useEffect, useState } from 'react'
import {
  processMiningApi,
  type ProcessBaseline,
  type ProcessGroupBy,
  type ProcessMiningReport,
} from '../api/processMining'
import { Card, CardHeader, Callout, TextField, Select, Page, PageHeader, DataTable, StatusPill, CircularProgress, LinearProgress, FilterBar } from '../components/ui'

/**
 * F-061 (TASK-0315) — process mining: anomalie in cima, barre per fase con quota %, baseline e tabella
 * segmenti. Nessun importo. Soglie (scostamento e campione minimo) PROVVISORIE. La fase di incasso non è
 * misurata e il report lo dichiara. La pagina non fa gating di ruolo: il gate è il Manager del backend,
 * un caller non autorizzato vede solo il messaggio di errore.
 */

const GROUP_LABELS: Record<ProcessGroupBy, string> = {
  agent: 'Agente',
  team: 'Team',
  operator: 'Operatore',
  department: 'Reparto',
}

const BASELINE_LABELS: Record<string, string> = {
  last30: 'Ultimi 30 giorni',
  last90: 'Ultimi 90 giorni',
  samePeriodLastYear: 'Stesso periodo anno scorso',
}

function formatHours(v: number | null): string {
  if (v === null) return '—'
  if (v >= 48) return `${(v / 24).toLocaleString('it-IT', { maximumFractionDigits: 1 })} giorni`
  return `${v.toLocaleString('it-IT', { maximumFractionDigits: 1 })} ore`
}

const formatPct = (v: number | null): string =>
  v === null ? '—' : `${v.toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`

export function ProcessMiningPage() {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [groupBy, setGroupBy] = useState<ProcessGroupBy>('agent')
  const [deviationPct, setDeviationPct] = useState('50')
  const [minSample, setMinSample] = useState('5')
  const [report, setReport] = useState<ProcessMiningReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const dev = Number(deviationPct)
      const min = Number(minSample)
      setReport(
        await processMiningApi.get({
          from: from || undefined,
          to: to || undefined,
          groupBy,
          deviationPct: Number.isFinite(dev) && deviationPct !== '' ? dev : undefined,
          minSample: Number.isFinite(min) && minSample !== '' ? min : undefined,
        }),
      )
    } catch (e) {
      setReport(null)
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [from, to, groupBy, deviationPct, minSample])

  useEffect(() => {
    void load()
    // Caricamento iniziale: i filtri successivi si applicano col pulsante.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const maxShare = report ? Math.max(1, ...report.phases.map(p => p.sharePct ?? 0)) : 1

  const baselineMedians = (b: ProcessBaseline) =>
    report!.phases.map(p => b.phases.find(x => x.phaseKey === p.phaseKey && x.n > 0)?.medianHours ?? null)

  return (
    <Page>
      <PageHeader
        title="Process mining"
        icon="account_tree"
        actions={<ReportExportButtons title="Process mining" />}
        subtitle={
          report ? (
            <span className="text-muted md-typescale-body-small">
              {report.from.slice(0, 10)} - {report.to.slice(0, 10)}
            </span>
          ) : undefined
        }
      />

      <FilterBar onSubmit={() => void load()}>
        <TextField label="Da" type="date" value={from} onChange={e => setFrom(e.target.value)} />
        <TextField label="A" type="date" value={to} onChange={e => setTo(e.target.value)} />
        <Select label="Segmenta per" value={groupBy} onChange={e => setGroupBy(e.target.value as ProcessGroupBy)}>
          {(Object.keys(GROUP_LABELS) as ProcessGroupBy[]).map(g => (
            <option key={g} value={g}>{GROUP_LABELS[g]}</option>
          ))}
        </Select>
        <TextField label="Scostamento % (provvisorio)" type="number" inputMode="numeric" min={0} value={deviationPct} onChange={e => setDeviationPct(e.target.value)} />
        <TextField label="Campione minimo (provvisorio)" type="number" inputMode="numeric" min={1} value={minSample} onChange={e => setMinSample(e.target.value)} />
      </FilterBar>
      <div className="text-muted md-typescale-body-small">Senza date: ultimi 30 giorni. Massimo 366 giorni.</div>

      {loading ? (
        <div className="text-center py-4"><div className="d-inline-block"><CircularProgress size="md" /></div></div>
      ) : error ? (
        <Callout tone="warning">Process mining non disponibile. {error}</Callout>
      ) : report ? (
        <>
          <Card className="mb-3">
            <CardHeader title="Anomalie" />
            <div className="p-4">
              {report.anomalies.length === 0 ? (
                <div className="text-muted md-typescale-body-small">Nessuna anomalia nel periodo con le soglie correnti.</div>
              ) : (
                <ul className="list-unstyled">
                  {report.anomalies.map((a, i) => (
                    <li key={`${a.kind}-${a.phaseKey}-${a.segmentKey ?? a.baseline}-${i}`} className="mb-2 pb-2 border-bottom last:border-b-0">
                      <StatusPill tone={a.deviationPct >= 0 ? 'error' : 'info'} className="me-2">
                        {a.deviationPct >= 0 ? '+' : ''}{a.deviationPct.toLocaleString('it-IT')}%
                      </StatusPill>
                      {a.title}
                      <span className="text-muted md-typescale-body-small ms-2">
                        {a.kind === 'segment' ? `(${a.phaseTitle})` : '(contro baseline)'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>

          <Card className="mb-3">
            <CardHeader
              title="Durata per fase"
              subtitle={
                report.bottleneck ? (
                  <span className="md-typescale-body-small">
                    Collo di bottiglia: <strong>{report.bottleneck.title}</strong> ({formatPct(report.bottleneck.sharePct)})
                  </span>
                ) : undefined
              }
            />
            <div className="p-4">
              {report.phases.map(p => (
                <div key={p.phaseKey} className="mb-3">
                  <div className="d-flex justify-content-between md-typescale-body-small">
                    <span className={report.bottleneck?.phaseKey === p.phaseKey ? 'text-danger' : undefined}>{p.title}</span>
                    <span className="text-muted">
                      {p.n === 0
                        ? 'nessun dato'
                        : `mediana ${formatHours(p.medianHours)} · p75 ${formatHours(p.p75Hours)} · p90 ${formatHours(p.p90Hours)} · n=${p.n} · ${formatPct(p.sharePct)}`}
                    </span>
                  </div>
                  <LinearProgress
                    value={((p.sharePct ?? 0) / maxShare) * 100}
                    aria-label={`${p.title}${report.bottleneck?.phaseKey === p.phaseKey ? ' (collo di bottiglia)' : ''}: ${formatPct(p.sharePct)}`}
                  />
                </div>
              ))}
              {report.gaps.length > 0 && (
                <ul className="md-typescale-body-small text-muted mt-3 mb-0 list-unstyled">
                  {report.gaps.map(g => <li key={g}>{g}</li>)}
                </ul>
              )}
              <div className="md-typescale-body-small text-muted mt-2">
                Escluse: {report.excluded.migrati} pratiche migrate senza transizioni vere; {report.excluded.negativi} durate negative scartate.
              </div>
            </div>
          </Card>

          <Card className="mb-3">
            <CardHeader title="Confronto con le baseline (mediana)" />
            <DataTable variant="plain">
              <thead>
                <tr>
                  <th>Fase</th>
                  <th className="text-end md-num">Periodo</th>
                  {(['last30', 'last90', 'samePeriodLastYear'] as const).map(k => (
                    <th key={k} className="text-end md-num">{BASELINE_LABELS[k]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.phases.map((p, i) => (
                  <tr key={p.phaseKey}>
                    <td>{p.title}</td>
                    <td className="text-end md-num">{formatHours(p.medianHours)}</td>
                    {(['last30', 'last90', 'samePeriodLastYear'] as const).map(k => (
                      <td key={k} className="text-end md-num">{formatHours(baselineMedians(report.baselines[k])[i] ?? null)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </Card>

          <Card className="mb-3">
            <CardHeader title={`Segmenti (${GROUP_LABELS[report.groupBy]})`} />
            {report.segments.length === 0 ? (
              <div className="p-4 text-muted md-typescale-body-small">Nessun segmento con dati nel periodo.</div>
            ) : (
              <DataTable variant="plain">
                <thead>
                  <tr>
                    <th>Fase</th>
                    <th>{GROUP_LABELS[report.groupBy]}</th>
                    <th className="text-end md-num">n</th>
                    <th className="text-end md-num">Mediana</th>
                    <th className="text-end md-num">Media globale (mediana)</th>
                    <th className="text-end md-num">Rapporto</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {report.segments.map(s => (
                    <tr key={`${s.phaseKey}-${s.segmentKey}`} className={s.anomalous ? 'table-danger' : ''}>
                      <td>{s.phaseTitle}</td>
                      <td>{s.segmentLabel}</td>
                      <td className="text-end md-num">{s.n}</td>
                      <td className="text-end md-num">{formatHours(s.medianHours)}</td>
                      <td className="text-end md-num">{formatHours(s.globalMedianHours)}</td>
                      <td className="text-end md-num">
                        {s.ratioVsGlobal === null ? '—' : `${s.ratioVsGlobal.toLocaleString('it-IT')}×`}
                      </td>
                      <td>{s.anomalous && <StatusPill tone="error">Anomalo</StatusPill>}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            )}
          </Card>
        </>
      ) : null}
    </Page>
  )
}
